import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import type { U2000SearchPayload, U2000SearchResult } from "../../../../u2000Core/components/tables/basic/U2000BasicTable";
import {
  doGetDocumentsList,
  doCreateDocument,
  doDeleteDocument,
  doPatchDocumentName,
  doSearch,
  luHandleAppiError,
  type U3141LotDetailDocument,
  type U3141MasterLotChain,
} from "../../../U3141Service";
import { NS } from "../../../U3141";

/** Handlers pour charger la liste des documents (GET documents). */
export function useU3141GetDocumentsHandlers(params: {
  masterLotChainId: string | number;
  childLotChainId: string | number;
  onSuccess?: (list: U3141LotDetailDocument[]) => void;
}) {
  const { masterLotChainId, childLotChainId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadDocuments = useCallback(async () => {
    try {
      const list = await doGetDocumentsList(axios, masterLotChainId, childLotChainId);
      onSuccess?.(list);
      return list;
    } catch (error: unknown) {
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
      return [];
    }
  }, [axios, masterLotChainId, childLotChainId, onSuccess, t, toast]);

  return { loadDocuments };
}

/** Handlers pour créer un document (POST multipart). */
export function useU3141AddDocumentHandlers(params: {
  masterLotChainId: string | number;
  childLotChainId: string | number;
  onSuccess?: () => void;
}) {
  const { masterLotChainId, childLotChainId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const createDocument = useCallback(
    async (formData: FormData) => {
      return doCreateDocument(axios, masterLotChainId, childLotChainId, formData);
    },
    [axios, masterLotChainId, childLotChainId],
  );

  const addDocument = useCallback(
    async (formData: FormData) => {
      try {
        await createDocument(formData);
        toast.success(t("msgDocumentAdded", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [createDocument, onSuccess, t, toast],
  );

  return { addDocument, createDocument };
}

/** Handlers pour supprimer un document (DELETE). */
export function useU3141DeleteDocumentHandlers(params: {
  masterLotChainId: string | number;
  childLotChainId: string | number;
  onSuccess?: () => void;
}) {
  const { masterLotChainId, childLotChainId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const deleteDocument = useCallback(
    async (documentId: string | number) => {
      try {
        await doDeleteDocument(axios, masterLotChainId, childLotChainId, documentId);
        toast.success(t("msgDocumentDeleted", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, masterLotChainId, childLotChainId, onSuccess, t, toast]
  );

  return { deleteDocument };
}

/** Handlers pour renommer un document (PATCH documentName). */
export function useU3141PatchDocumentNameHandlers(params: {
  masterLotChainId: string | number;
  childLotChainId: string | number;
  onSuccess?: () => void;
}) {
  const { masterLotChainId, childLotChainId, onSuccess } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const patchDocumentName = useCallback(
    async (documentId: string | number, documentName: string) => {
      try {
        await doPatchDocumentName(axios, masterLotChainId, childLotChainId, documentId, documentName);
        toast.success(t("msgDocumentRenamed", { ns: NS }));
        onSuccess?.();
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, masterLotChainId, childLotChainId, onSuccess, t, toast]
  );

  return { patchDocumentName };
}

/**
 * Handlers pour la recherche de lots maîtres (projets) dans le popup Add document.
 * Expose search(searchString) => Promise<U3141MasterLotChain[]>.
 */
export function useU3141SearchMasterLotChainHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (searchString: string): Promise<U3141MasterLotChain[]> => {
      if (!searchString || searchString.trim().length < 2) return [];
      try {
        const payload: U2000SearchPayload = {
          filter: {
            criterias: [{ field: "projectNumber", operator: "contains", value: searchString.trim(), type: "string" }],
            fields: [{ name: "projectNumber" }, { name: "projectDescription" }, { name: "id" }],
            sortedFields: [],
          },
          pagination: { page: 0, size: 20 },
        };
        const res = await doSearch(axios, payload);
        const data = (res.data as U2000SearchResult<U3141MasterLotChain>)?.data;
        return Array.isArray(data) ? data : [];
      } catch (error: unknown) {
        const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
        if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        return [];
      }
    },
    [axios, t, toast]
  );

  return { search };
}
